import DrawIcon from '@mui/icons-material/Draw';
import { LoadingButton } from '@mui/lab';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Box, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Field, FieldGrid, SectionCard } from '../../../../components/Section';
import { TREASURY_OWNER_AGREEMENT_CHECKS } from '../../../../constants/treasury';
import { usePermissions } from '../../../../hooks';
import {
  useFetchTreasuryOwnerAgreementQuery,
  useSignTreasuryOwnerAgreementMutation,
} from '../../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryCompanySetup, TreasuryOwnerAgreementSignature } from '../../../../types/treasury';
import * as permissions from '../../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../../utils/treasury';

type TreasuryOwnerAgreementSectionProps = {
  setup: TreasuryCompanySetup;
};

/**
 * The owner's own account opening agreement — the admin's twin of the mobile app's "Sign Agreement" screen, with the
 * same three checkboxes, signed for the owner under the company contract. madkhol-user then moves the owner's status
 * (Active opens their personal Alpaca account). The company Alpaca account waits for it
 * (readiness USER_SIGN_ACCOUNT_OPENING_AGREEMENT_NOT_FOUND / OWNER_USER_NOT_ACTIVE).
 */
export const TreasuryOwnerAgreementSection = ({ setup }: TreasuryOwnerAgreementSectionProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canSign = usePermissions({ items: [permissions.Treasury.approveEmployee] });
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const { employeeId } = setup;
  const { data, isLoading, isError, error } = useFetchTreasuryOwnerAgreementQuery(
    { companyId: setup.companyId, employeeId: employeeId ?? 0 },
    { skip: !employeeId },
  );
  const signature: TreasuryOwnerAgreementSignature | null = data ?? null;
  // Signing refetches the signature (and the readiness the Alpaca step reads).
  const [signTreasuryOwnerAgreement, { isLoading: isSigning }] = useSignTreasuryOwnerAgreementMutation();

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(getTreasuryErrorMessage(error, "Failed to read the owner's agreement"), { variant: 'error' });
    }
  }, [isError, error, enqueueSnackbar]);

  const sign = async () => {
    if (!employeeId) return;

    try {
      await signTreasuryOwnerAgreement({ companyId: setup.companyId, employeeId }).unwrap();
      enqueueSnackbar("The owner's account opening agreement is signed.", { variant: 'success' });
    } catch (signError) {
      enqueueSnackbar(getTreasuryErrorMessage(signError, 'Agreement sign error!'), { variant: 'error' });
    }
  };

  const allChecked = TREASURY_OWNER_AGREEMENT_CHECKS.every((check) => checks[check.key]);

  const statusChip = signature ? (
    <Chip size="small" color="success" label="Signed" />
  ) : (
    <Chip size="small" color="warning" label="Not signed" />
  );

  return (
    <SectionCard
      title="Owner's Account Opening Agreement"
      description={`What ${
        setup.employee?.full_name_en ?? 'the owner'
      } signs in the app — signed here for them under the company contract.`}
      action={!isLoading && statusChip}
    >
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && signature && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap">
          <FieldGrid columns={1}>
            <Field label="Signed On" value={new Date(signature.updatedAt ?? signature.createdAt).toLocaleString()} />
          </FieldGrid>
          <Tooltip title="Records your IP on the owner's account when it has none — their personal Alpaca account needs it.">
            <span>
              <LoadingButton
                variant="outlined"
                size="small"
                loading={isSigning}
                startIcon={<DrawIcon />}
                onClick={sign}
                disabled={isSigning || !canSign || !employeeId}
              >
                Sign Again
              </LoadingButton>
            </span>
          </Tooltip>
        </Stack>
      )}

      {!isLoading && !signature && (
        <Stack spacing={1}>
          {TREASURY_OWNER_AGREEMENT_CHECKS.map((check) => (
            <FormControlLabel
              key={check.key}
              control={
                <Checkbox
                  id={check.key}
                  name={check.key}
                  checked={Boolean(checks[check.key])}
                  onChange={(event) => setChecks((prev) => ({ ...prev, [check.key]: event.target.checked }))}
                />
              }
              label={<Typography variant="body2">{check.label}</Typography>}
            />
          ))}
          <Typography variant="caption" color="text.secondary">
            Signing changes the owner&apos;s account right away, as in the app: Active when World-Check is clean and
            auto-activation is on (this opens their personal Alpaca account), otherwise Pending — or Manual Check
            Required on a World-Check hit.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isSigning}
              startIcon={<DrawIcon />}
              onClick={sign}
              disabled={!allChecked || isSigning || !canSign || !employeeId}
            >
              Sign for the Owner
            </LoadingButton>
          </Box>
        </Stack>
      )}
    </SectionCard>
  );
};
